/**
 * Платформні реалізації портів часу й ідентифікаторів.
 *
 * Логіка (UUIDv7, HLC) живе в core і не знає про браузер. Тут лишається
 * рівно те, що є платформою: системний годинник, джерело випадковості
 * і сталий ідентифікатор пристрою.
 */
import {
  type ClockPort,
  type HlcState,
  type IdPort,
  makeHlcClock,
  makeUuidV7,
  today,
} from "@beresta/core";

const DEVICE_KEY = "beresta.deviceId";

/**
 * Ідентифікатор пристрою переживає перезавантаження, але не очищення даних.
 *
 * Це прийнятно: він лише розвʼязує нічиї в порядку злиття. Новий
 * ідентифікатор після очищення означає, що пристрій виглядає як інший —
 * дані від цього не втрачаються, лише нічия розвʼязується інакше.
 */
export function deviceId(): string {
  try {
    const seen = localStorage.getItem(DEVICE_KEY);
    if (seen) return seen;
  } catch {
    // Приватне вікно або заблоковані дані сайту. Не привід не працювати.
  }
  const fresh = randomHex(8);
  try {
    localStorage.setItem(DEVICE_KEY, fresh);
  } catch {
    // Тоді ідентифікатор живе лише цю сесію.
  }
  return fresh;
}

function randomHex(bytes: number): string {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export function createClock(): ClockPort {
  return {
    now: () => Date.now(),
    // Локальна дата, а не UTC: запис о 23:30 належить сьогоднішньому дню
    // користувача, а не завтрашньому за Гринвічем.
    todayKey: () => today().toString(),
  };
}

/**
 * Ідентифікатор цього пристрою **для сервера** — окремий від того, що
 * стоїть у хвості мітки HLC.
 *
 * Їх два не з примхи. Серверу потрібен UUID: `POST /v1/account`
 * і `POST /v1/session` перевіряють формат. Мітці HLC потрібна стислість —
 * вона їде в кожній дельті, і 37 символів проти 128 дозволених дають
 * запас, якого UUID не дав би. Змінити локальний означало б переписати
 * хвіст кожної вже збереженої мітки заради нічого.
 */
const SERVER_DEVICE_KEY = "beresta.serverDeviceId";

export function serverDeviceId(): string {
  try {
    const seen = localStorage.getItem(SERVER_DEVICE_KEY);
    if (seen) return seen;
  } catch {
    // Приватне вікно. Тоді ідентифікатор живе лише цю сесію, і сервер
    // побачить новий пристрій — це не втрата даних, лише зайвий рядок.
  }
  const fresh = crypto.randomUUID();
  try {
    localStorage.setItem(SERVER_DEVICE_KEY, fresh);
  } catch {
    // Те саме.
  }
  return fresh;
}

/**
 * `initial` піднімає годинник із того місця, де він спинився: стан HLC
 * лежить у `sync_state` і читається при старті. Без нього кожен запуск
 * починав би з нуля, і після переведення годинника назад свіжа правка
 * отримала б меншу мітку за стару.
 */
/** `IdPort` плюс доступ до стану годинника — щоб його можна було зберегти. */
export interface AppIds extends IdPort {
  state(): HlcState;
}

export function createIds(device: string = deviceId(), initial?: HlcState): AppIds {
  const uuid = makeUuidV7((n) => crypto.getRandomValues(new Uint8Array(n)));
  const hlc = makeHlcClock(device, Date.now, initial);
  return {
    next: () => uuid(),
    nextHlc: () => hlc.next(),
    receiveHlc: (remote) => hlc.receive(remote),
    deviceId: () => device,
    // Стан назовні — щоб його можна було зберегти між запусками.
    state: () => hlc.state,
  };
}
